import { createElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';

import { moduleLabel, PALETTE_MODULES, useResultIcon } from '../modules';
import { PALETTE_PERIODS, usePaletteFilters } from '../paletteFilters';

const chipClass = (selected: boolean) =>
  cn(
    'inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium whitespace-nowrap focus-ring transition-colors',
    selected
      ? 'border-transparent bg-foreground text-background'
      : 'bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
  );

/**
 * A chip of the palette's filter row. A mouse click must not move the focus out of the palette's input (typing goes on);
 * the keyboard reaches the chips with Tab and toggles them with Enter or Space.
 */
function Chip({
  selected,
  onSelect,
  children,
  testId,
  value,
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
  testId: string;
  value?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onSelect}
      data-testid={testId}
      data-value={value}
      className={chipClass(selected)}
    >
      {children}
    </button>
  );
}

function ModuleChip({ module }: { module: string }) {
  const { i18n } = useTranslation('search');
  const selected = usePaletteFilters((s) => s.module === module);
  const setModule = usePaletteFilters((s) => s.setModule);
  const Icon = useResultIcon(module);
  return (
    <Chip
      selected={selected}
      onSelect={() => setModule(selected ? null : module)}
      testId="palette-filter-module"
      value={module}
    >
      {createElement(Icon, { className: 'size-3.5', 'aria-hidden': true })}
      {moduleLabel(module, i18n)}
    </Chip>
  );
}

/**
 * The filter row of the ⌘K palette (the search module's `toolbar`): the section (one of the palette's groups), when it
 * changed (24 hours, 7 days, 30 days) and "only mine". It edits {@link usePaletteFilters}, which the live results and the
 * "search everywhere" link read.
 */
export function PaletteFilters() {
  const { t } = useTranslation('search');
  const module = usePaletteFilters((s) => s.module);
  const period = usePaletteFilters((s) => s.period);
  const mine = usePaletteFilters((s) => s.mine);
  const { setModule, setPeriod, setMine } = usePaletteFilters.getState();

  return (
    <div
      role="group"
      aria-label={t('palette.filters.label')}
      className="space-y-1.5 border-b px-3 py-2"
      data-testid="palette-filters"
      // The palette runs the selected entry on Enter (and Space is the button's own): a chip must keep both for itself.
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
    >
      <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 pb-0.5" data-testid="palette-filter-modules">
        <Chip selected={module === null} onSelect={() => setModule(null)} testId="palette-filter-all">
          {t('palette.filters.all')}
        </Chip>
        {PALETTE_MODULES.map((id) => (
          <ModuleChip key={id} module={id} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="palette-filter-periods">
        <Chip
          selected={period === null}
          onSelect={() => setPeriod(null)}
          testId="palette-filter-period"
          value="any"
        >
          {t('palette.filters.periodAny')}
        </Chip>
        {PALETTE_PERIODS.map((preset) => (
          <Chip
            key={preset}
            selected={period === preset}
            onSelect={() => setPeriod(period === preset ? null : preset)}
            testId="palette-filter-period"
            value={preset}
          >
            {t(`palette.filters.${preset}`)}
          </Chip>
        ))}
        <span aria-hidden className="mx-1 h-4 w-px bg-border" />
        <Chip selected={mine} onSelect={() => setMine(!mine)} testId="palette-filter-mine">
          {t('palette.filters.mine')}
        </Chip>
      </div>
    </div>
  );
}
